import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getJSON } from '@/lib/api'

interface Person {
  id: number
  name: string
  email: string | null
  contact_type: string
}

export function EnrolmentPersonPicker({
  value,
  onChange,
  error,
}: {
  value: number | null
  onChange: (id: number | null) => void
  error: string
}) {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Person | null>(null)
  const query = useQuery({
    queryKey: ['enrolment-people', search],
    queryFn: () =>
      getJSON<{ contacts: Person[] }>(
        `/api/v1/contacts?contact_type=person&search=${encodeURIComponent(search)}`,
      ),
  })
  return (
    <FormField label="Person" htmlFor="enrolment-person" error={error}>
      <Input
        id="enrolment-person"
        placeholder="Search people…"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value)
          onChange(null)
          setSelected(null)
        }}
      />
      {selected && <p className="text-body-sm">{selected.name}</p>}
      {!value && (
        <div className="max-h-40 space-y-1 overflow-y-auto">
          {query.isPending && <p className="text-body-sm text-muted-foreground">Loading people…</p>}
          {query.isError && <Alert>{query.error.message}</Alert>}
          {query.data?.contacts.length === 0 && (
            <p className="text-body-sm text-muted-foreground">No matching people</p>
          )}
          {query.data?.contacts
            .filter((person) => person.contact_type === 'Person')
            .map((person) => (
              <Button
                key={person.id}
                type="button"
                variant="outline"
                className="w-full justify-start"
                onClick={() => {
                  setSelected(person)
                  onChange(person.id)
                }}
              >
                {person.name}
                {person.email && ` · ${person.email}`}
              </Button>
            ))}
        </div>
      )}
    </FormField>
  )
}
